'use client';

import { useState, useRef, useEffect } from 'react';
import { URI } from '../../../../domain/constant';

// Enhanced GalleryCard component with better multiple image handling
function GalleryCard({ item }) {
  const [imgIndex, setImgIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const images = Array.isArray(item.image) ? item.image : [item.image];
  const hasMultiple = images.length > 1;

  const nextImg = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev + 1) % images.length);
  };

  const prevImg = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index, e) => {
    e.stopPropagation();
    setImgIndex(index);
  };

  const openImageModal = () => {
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  // Touch handling for mobile swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && hasMultiple) {
      setImgIndex((prev) => (prev + 1) % images.length);
    }
    if (isRightSwipe && hasMultiple) {
      setImgIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <>
      <div className='gallery-card default-card'>
        <div className='card-image-container'>
          <div
            className='image-wrapper'
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onClick={openImageModal}
          >
            <img
              src={
                `${URI}${images[imgIndex]}` ||
                '/placeholder.svg?height=200&width=320'
              }
              alt={item.description?.slice(0, 20) || 'Gallery Image'}
              className='card-image'
            />

            {/* Image Counter Badge */}
            {hasMultiple && (
              <div className='image-counter-badge'>
                {imgIndex + 1} / {images.length}
              </div>
            )}

            {/* Carousel Controls */}
            {hasMultiple && (
              <div className='img-carousel-controls'>
                <button onClick={prevImg} className='img-carousel-btn left'>
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='3'
                  >
                    <polyline points='15,18 9,12 15,6'></polyline>
                  </svg>
                </button>
                <button onClick={nextImg} className='img-carousel-btn right'>
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='3'
                  >
                    <polyline points='9,18 15,12 9,6'></polyline>
                  </svg>
                </button>
              </div>
            )}

            {/* Image Indicators */}
            {hasMultiple && (
              <div className='image-indicators'>
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator-dot ${
                      index === imgIndex ? 'active' : ''
                    }`}
                    onClick={(e) => goToImage(index, e)}
                  />
                ))}
              </div>
            )}

            {/* Zoom Icon */}
            <div className='zoom-icon'>
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <circle cx='11' cy='11' r='8'></circle>
                <path d='21 21l-4.35-4.35'></path>
                <line x1='11' y1='8' x2='11' y2='14'></line>
                <line x1='8' y1='11' x2='14' y2='11'></line>
              </svg>
            </div>
          </div>
        </div>

        <div className='card-body'>
          <div className='card-description'>
            <p>{item.description}</p>
          </div>
          <div className='card-meta'>
            <div className='author-info'>
              <span className='author-label'>By:</span>
              <span className='author-name'>{item.author}</span>
            </div>
            {item.createdAt && (
              <div className='date-info'>
                {new Date(item.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div className='image-modal-overlay' onClick={closeImageModal}>
          <div
            className='image-modal-content'
            onClick={(e) => e.stopPropagation()}
          >
            <button className='modal-close-btn' onClick={closeImageModal}>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <line x1='18' y1='6' x2='6' y2='18'></line>
                <line x1='6' y1='6' x2='18' y2='18'></line>
              </svg>
            </button>

            <div className='modal-image-container'>
              <img
                src={`${URI}${images[imgIndex]}`}
                alt={item.description}
                className='modal-image'
              />

              {hasMultiple && (
                <>
                  <button onClick={prevImg} className='modal-nav-btn left'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                    >
                      <polyline points='15,18 9,12 15,6'></polyline>
                    </svg>
                  </button>
                  <button onClick={nextImg} className='modal-nav-btn right'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                    >
                      <polyline points='9,18 15,12 9,6'></polyline>
                    </svg>
                  </button>

                  <div className='modal-indicators'>
                    {images.map((_, index) => (
                      <button
                        key={index}
                        className={`modal-indicator-dot ${
                          index === imgIndex ? 'active' : ''
                        }`}
                        onClick={(e) => goToImage(index, e)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className='modal-info'>
              <h3>{item.description}</h3>
              <p>
                By {item.author} •{' '}
                {hasMultiple
                  ? `${imgIndex + 1} of ${images.length}`
                  : '1 image'}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function HorizontalGallery({ items, title = 'Gallery' }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const scroll = (direction) => {
    const container = scrollRef.current;
    const scrollAmount = 300; // Reduced for more compact design

    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      handleScroll(); // Initial check
    }
  }, [items]);

  if (!items || items.length === 0) {
    return (
      <div className='horizontal-gallery-container'>
        <div className='gallery-header'>
          <div className='header-content'>
            <h3 className='gallery-title'>{title}</h3>
            <div className='gallery-subtitle'>No items to display</div>
          </div>
        </div>
        <div className='empty-gallery'>
          <div className='empty-icon'>🖼️</div>
          <p>No gallery items found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='horizontal-gallery-container'>
        <div className='gallery-header'>
          <div className='header-content'>
            <h3 className='gallery-title'>{title}</h3>
            <div className='gallery-subtitle'>
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </div>
          </div>
          <div className='gallery-controls'>
            <button
              className={`gallery-btn ${!canScrollLeft ? 'disabled' : ''}`}
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <polyline points='15,18 9,12 15,6'></polyline>
              </svg>
            </button>
            <button
              className={`gallery-btn ${!canScrollRight ? 'disabled' : ''}`}
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <polyline points='9,18 15,12 9,6'></polyline>
              </svg>
            </button>
          </div>
        </div>

        <div
          className='gallery-scroll-container'
          ref={scrollRef}
          onScroll={handleScroll}
        >
          <div className='gallery-items'>
            {items.map((item, index) => (
              <div key={item._id || index} className='gallery-item'>
                <GalleryCard item={item} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .horizontal-gallery-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          overflow: hidden;
          margin-bottom: 20px;
          border: 1px solid #f1f5f9;
        }

        .gallery-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 24px;
          background: linear-gradient(
            135deg,
            #1e293b 0%,
            #334155 25%,
            #dc2626 75%,
            #b91c1c 100%
          );
          color: white;
          position: relative;
        }

        .gallery-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.08) 0%,
            transparent 50%
          );
          pointer-events: none;
        }

        .header-content {
          z-index: 1;
        }

        .gallery-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 2px 0;
          letter-spacing: -0.025em;
        }

        .gallery-subtitle {
          font-size: 13px;
          opacity: 0.85;
          font-weight: 400;
        }

        .gallery-controls {
          display: flex;
          gap: 6px;
          z-index: 1;
        }

        .gallery-btn {
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(8px);
        }

        .gallery-btn:hover:not(.disabled) {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-1px);
        }

        .gallery-btn.disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .gallery-scroll-container {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding: 20px 0 24px;
        }

        .gallery-scroll-container::-webkit-scrollbar {
          display: none;
        }

        .gallery-items {
          display: flex;
          gap: 16px;
          padding: 0 24px;
          min-width: fit-content;
        }

        .gallery-item {
          flex-shrink: 0;
        }

        .gallery-card {
          width: 280px;
          height: 340px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid #f1f5f9;
          display: flex;
          flex-direction: column;
          position: relative;
        }

        .gallery-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
          border-color: #e2e8f0;
        }

        .gallery-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #dc2626, #b91c1c);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .gallery-card:hover::before {
          opacity: 1;
        }

        .card-image-container {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .image-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        .card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .gallery-card:hover .card-image {
          transform: scale(1.03);
        }

        .image-counter-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(0, 0, 0, 0.75);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          backdrop-filter: blur(8px);
        }

        .img-carousel-controls {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          pointer-events: none;
          transform: translateY(-50%);
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .gallery-card:hover .img-carousel-controls {
          opacity: 1;
        }

        .img-carousel-btn {
          pointer-events: auto;
          background: rgba(0, 0, 0, 0.6);
          color: white;
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          backdrop-filter: blur(8px);
        }

        .img-carousel-btn:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.1);
        }

        .img-carousel-btn.left {
          margin-left: 12px;
        }

        .img-carousel-btn.right {
          margin-right: 12px;
        }

        .image-indicators {
          position: absolute;
          bottom: 12px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          pointer-events: none;
        }

        .indicator-dot {
          pointer-events: auto;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .indicator-dot.active {
          background: white;
          transform: scale(1.2);
        }

        .zoom-icon {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(0, 0, 0, 0.6);
          color: white;
          padding: 6px;
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.2s ease;
          backdrop-filter: blur(8px);
        }

        .gallery-card:hover .zoom-icon {
          opacity: 1;
        }

        .card-body {
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .card-description {
          flex: 1;
          margin-bottom: 12px;
        }

        .card-description p {
          font-size: 14px;
          color: #4b5563;
          line-height: 1.5;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: auto;
        }

        .author-info {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .author-label {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 500;
        }

        .author-name {
          font-size: 12px;
          color: #dc2626;
          font-weight: 600;
        }

        .date-info {
          font-size: 11px;
          color: #9ca3af;
        }

        .empty-gallery {
          padding: 60px 20px;
          text-align: center;
          color: #6b7280;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
          opacity: 0.5;
        }

        /* Image Modal Styles */
        .image-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .image-modal-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.6);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          transition: background 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(0, 0, 0, 0.8);
        }

        .modal-image-container {
          position: relative;
          max-height: 70vh;
          overflow: hidden;
        }

        .modal-image {
          width: 100%;
          height: auto;
          max-height: 70vh;
          object-fit: contain;
        }

        .modal-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.6);
          color: white;
          border: none;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .modal-nav-btn:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
        }

        .modal-nav-btn.left {
          left: 16px;
        }

        .modal-nav-btn.right {
          right: 16px;
        }

        .modal-indicators {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
        }

        .modal-indicator-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-indicator-dot.active {
          background: white;
          transform: scale(1.2);
        }

        .modal-info {
          padding: 20px;
          background: white;
        }

        .modal-info h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
        }

        .modal-info p {
          margin: 0;
          font-size: 14px;
          color: #6b7280;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .gallery-items {
            padding: 0 16px;
            gap: 12px;
          }

          .gallery-card {
            width: 240px;
            height: 300px;
          }

          .card-image-container {
            height: 160px;
          }

          .gallery-header {
            padding: 16px 20px;
          }

          .gallery-title {
            font-size: 16px;
          }

          .gallery-subtitle {
            font-size: 12px;
          }

          .gallery-scroll-container {
            padding: 16px 0 20px;
          }

          .modal-nav-btn {
            width: 40px;
            height: 40px;
          }

          .modal-nav-btn.left {
            left: 12px;
          }

          .modal-nav-btn.right {
            right: 12px;
          }
        }
      `}</style>
    </>
  );
}
